/**
 * Chat-panel state shared by the layout (which owns the resizable region)
 * and the topbar (which renders the toggle button).
 *
 * State:
 *   - open       : whether the side panel is visible at all
 *   - maximised  : whether the side panel covers the full content area
 *   - sizePct    : panel width as a % of the workspace, persisted across reloads
 *   - available  : whether the current route has a thread to bind to
 *   - mobileOpen : whether the phone-width assistant sheet is open (not
 *                  persisted — a phone opens it on demand, never on load)
 *
 * All persisted to localStorage so the user's preference survives reloads.
 */

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const CHAT_OPEN_KEY = "chai.chat-panel.open";
const CHAT_MAX_KEY = "chai.chat-panel.maximised";
const CHAT_SIZE_KEY = "chai.chat-panel.size-pct";

/** Sensible bounds for the resizable side panel (in % of viewport). */
export const CHAT_PANEL_MIN_PCT = 22;
export const CHAT_PANEL_MAX_PCT = 65;
export const CHAT_PANEL_DEFAULT_PCT = 30;

interface ChatPanelContextValue {
  open: boolean;
  setOpen: (next: boolean) => void;
  toggle: () => void;

  maximised: boolean;
  setMaximised: (next: boolean) => void;
  toggleMaximised: () => void;

  sizePct: number;
  setSizePct: (next: number) => void;

  available: boolean;
  setAvailable: (next: boolean) => void;

  /** The phone-width sheet (below the mobile breakpoint the panel is not
   *  docked at all). Held here rather than in the trigger that renders it,
   *  because that trigger remounts with every layout branch of the shell —
   *  an "open the agent panel" request arriving mid-navigation would be
   *  lost with it. */
  mobileOpen: boolean;
  setMobileOpen: (next: boolean) => void;
}

const ChatPanelCtx = createContext<ChatPanelContextValue | null>(null);

export function ChatPanelProvider({ children }: { children: ReactNode }) {
  const [open, setOpenState] = useState(() => readBool(CHAT_OPEN_KEY, true));
  const [maximised, setMaxState] = useState(() => readBool(CHAT_MAX_KEY, false));
  const [sizePct, setSizeState] = useState(() => readNumber(CHAT_SIZE_KEY, CHAT_PANEL_DEFAULT_PCT));
  const [available, setAvailable] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    writeBool(CHAT_OPEN_KEY, next);
  }, []);

  const toggle = useCallback(() => {
    setOpenState((prev) => {
      const next = !prev;
      writeBool(CHAT_OPEN_KEY, next);
      return next;
    });
  }, []);

  const setMaximised = useCallback((next: boolean) => {
    setMaxState(next);
    writeBool(CHAT_MAX_KEY, next);
  }, []);

  const toggleMaximised = useCallback(() => {
    setMaxState((prev) => {
      const next = !prev;
      writeBool(CHAT_MAX_KEY, next);
      return next;
    });
  }, []);

  const setSizePct = useCallback((next: number) => {
    const clamped = Math.min(CHAT_PANEL_MAX_PCT, Math.max(CHAT_PANEL_MIN_PCT, next));
    setSizeState(clamped);
    writeNumber(CHAT_SIZE_KEY, clamped);
  }, []);

  const value = useMemo(
    () => ({
      open,
      setOpen,
      toggle,
      maximised,
      setMaximised,
      toggleMaximised,
      sizePct,
      setSizePct,
      available,
      setAvailable,
      mobileOpen,
      setMobileOpen,
    }),
    [
      open,
      setOpen,
      toggle,
      maximised,
      setMaximised,
      toggleMaximised,
      sizePct,
      setSizePct,
      available,
      mobileOpen,
    ],
  );

  return <ChatPanelCtx.Provider value={value}>{children}</ChatPanelCtx.Provider>;
}

export function useChatPanel(): ChatPanelContextValue {
  const ctx = useContext(ChatPanelCtx);
  if (!ctx) throw new Error("useChatPanel must be used inside <ChatPanelProvider>");
  return ctx;
}

function readBool(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  try {
    const v = window.localStorage.getItem(key);
    if (v === null) return fallback;
    return v !== "false";
  } catch {
    return fallback;
  }
}

function writeBool(key: string, value: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* ignore */
  }
}

function readNumber(key: string, fallback: number): number {
  if (typeof window === "undefined") return fallback;
  try {
    const v = window.localStorage.getItem(key);
    if (!v) return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function writeNumber(key: string, value: number) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* ignore */
  }
}
